import { NumberInput, type NumberInputProps } from '@any-ui-react/core';
import { useUncontrolled } from '@mantine/hooks';
import { forwardRef, useLayoutEffect, useMemo, useRef, useState } from 'react';

interface InputPriceProps extends Omit<NumberInputProps, 'defaultValue' | 'value' | 'onChange'> {
  currency?: string;
  defaultValue?: string;
  value?: string;
  onChange?: (e: string) => void;
}

const isDecimalScaleSpecified = (decimalScale: number | undefined): decimalScale is number =>
  !!decimalScale && decimalScale > 0;

const DEFAULT_DECIMAL_SCALE = 2;

export const InputPrice = forwardRef<HTMLInputElement, InputPriceProps>(
  ({ value: _value, defaultValue, currency, onChange, classNames, ...rest }: InputPriceProps, ref) => {
    const [value, handleChange] = useUncontrolled<string>({
      value: _value,
      defaultValue,
      finalValue: '',
      onChange,
    });

    const currencyRef = useRef<HTMLSpanElement>(null);
    const [rightSectionWidth, setRightSectionWidth] = useState(48);
    const decimalScale = useMemo(() => rest.decimalScale || DEFAULT_DECIMAL_SCALE, [rest.decimalScale]);

    useLayoutEffect(() => {
      if (currencyRef.current) {
        // 6px for padding
        setRightSectionWidth(currencyRef.current.offsetWidth + (currency && currency.length > 0 ? 6 : 0));
      }
    }, [currency]);

    return (
      <NumberInput
        ref={ref}
        classNames={{ ...classNames, input: 'text-right tabular-nums' }}
        decimalScale={rest.decimalScale || DEFAULT_DECIMAL_SCALE}
        decimalSeparator="."
        fixedDecimalScale={true}
        placeholder={isDecimalScaleSpecified(decimalScale) ? `0.${'0'.repeat(decimalScale)}` : '0'}
        rightSection={
          <span ref={currencyRef} className="border-l border-gray-2 px-2">
            {currency}
          </span>
        }
        rightSectionProps={{ className: 'w-max' }}
        rightSectionWidth={rightSectionWidth}
        step={isDecimalScaleSpecified(decimalScale) ? Number(`0.${'0'.repeat(decimalScale - 1)}1`) : 1}
        thousandSeparator=","
        value={value ? parseFloat(value) : 0}
        onChange={value => {
          handleChange(value.toString());
        }}
        {...rest}
      />
    );
  },
);
